import { Box, Button, CircularProgress, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { VpnKey as KeyIcon, Visibility, VisibilityOff } from "@mui/icons-material";
import { FormEvent, useState } from "react";

interface ResetPasswordProps {
  resetCode: string;
  newPassword: string;
  confirmPassword: string;
  email: string;
  loading: boolean;
  onResetCodeChange: (code: string) => void;
  onNewPasswordChange: (password: string) => void;
  onConfirmPasswordChange: (password: string) => void;
  onSubmit: (e: FormEvent) => void;
  onResendCode: () => void;
  onBackToLogin: () => void;
  formClassName: string;
  switchModeClassName: string;
}

const ResetPassword = ({
  resetCode,
  newPassword,
  confirmPassword,
  email,
  loading,
  onResetCodeChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
  onResendCode,
  onBackToLogin,
  formClassName,
  switchModeClassName
}: ResetPasswordProps) => {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const passwordsMatch = newPassword === confirmPassword;
  const isFormValid = resetCode.length === 6 && newPassword.length >= 6 && passwordsMatch;

  return (
    <>
      <Box mb={2}>
        <Typography variant="body2" color="text.secondary" align="center">
          Enter the 6-digit reset code sent to <strong>{email}</strong> and create a new password
        </Typography>
      </Box>
      
      <form className={formClassName} onSubmit={onSubmit}>
        <TextField
          label="Reset Code"
          value={resetCode}
          onChange={(e) => onResetCodeChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
          required
          fullWidth
          variant="outlined"
          placeholder="123456"
          inputProps={{ 
            style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' },
            maxLength: 6
          }}
        />
        
        <TextField
          label="New Password"
          type={showNewPassword ? "text" : "password"}
          value={newPassword}
          onChange={(e) => onNewPasswordChange(e.target.value)}
          required
          fullWidth
          variant="outlined"
          helperText="Password must be at least 8 characters long"
          error={newPassword.length > 0 && newPassword.length < 8}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  edge="end"
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          label="Confirm New Password"
          type={showConfirmPassword ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => onConfirmPasswordChange(e.target.value)}
          required
          fullWidth
          variant="outlined"
          error={confirmPassword.length > 0 && !passwordsMatch}
          helperText={confirmPassword.length > 0 && !passwordsMatch ? "Passwords don't match" : ""}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading || !isFormValid}
          startIcon={<KeyIcon />}
          sx={{ mt: 1 }}
        >
          {loading ? (
            <><CircularProgress size={20} sx={{ mr: 1 }} />Resetting Password...</>
          ) : (
            <>Reset Password</>
          )}
        </Button>
      </form>
      
      <Box textAlign="center" mt={2} display="flex" flexDirection="column" gap={1}>
        <Typography 
          variant="body2" 
          className={switchModeClassName}
          onClick={onResendCode}
        >
          Didn't receive the code? Resend
        </Typography>
        <Typography 
          variant="body2" 
          className={switchModeClassName}
          onClick={onBackToLogin}
        >
          Back to sign in
        </Typography>
      </Box>
    </>
  );
};

export default ResetPassword;